const DISMISS_KEY = 'jnr.pwa.install.oculto_ate';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

const INSTALL_COPY = {
  titulo: 'Instale o JNR NFS-e',
  corpo: 'Abra o emissor como aplicativo neste aparelho, com atalho na tela inicial.',
  aceitar: 'Instalar o aplicativo',
  recusar: 'Agora não',
} as const;

const IOS_COPY = {
  titulo: 'Instale o JNR NFS-e',
  corpo: 'No Safari, toque em Compartilhar e escolha Adicionar à Tela de Início.',
} as const;

const UPDATE_COPY = {
  titulo: 'Nova versão do JNR NFS-e',
  corpo: 'Atualize para continuar com o aplicativo em dia. A emissão já registrada no servidor não é interrompida.',
  aceitar: 'Atualizar agora',
} as const;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let clickBound = false;
let reloadOnControllerChange = false;
let swBound = false;

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isCalmRoute(path: string): boolean {
  if (path.startsWith('/emitir') || path.startsWith('/lotes')) {
    return false;
  }
  return true;
}

function dismissedUntil(): number {
  const raw = window.localStorage.getItem(DISMISS_KEY);
  if (!raw) {
    return 0;
  }
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : 0;
}

function dismissInstall(): void {
  window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
  hideHint();
}

function canRegisterSw(): boolean {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  const { protocol, hostname } = window.location;
  if (protocol === 'https:') {
    return true;
  }
  return protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1');
}

function ensureHint(): HTMLElement {
  let hint = document.querySelector<HTMLElement>('[data-pwa-install]');
  if (hint) {
    return hint;
  }
  hint = document.createElement('aside');
  hint.setAttribute('data-pwa-install', '');
  hint.className = 'pwa-hint';
  hint.setAttribute('role', 'status');
  hint.setAttribute('aria-live', 'polite');
  hint.hidden = true;
  hint.innerHTML =
    '<p class="banner__title" data-pwa-install-title></p><p data-pwa-install-body></p>' +
    '<div class="actions"><button class="btn-primary" type="button" data-pwa-install-accept></button>' +
    '<button class="btn-ghost" type="button" data-pwa-install-dismiss></button></div>';
  document.body.append(hint);
  return hint;
}

function ensureUpdate(): HTMLElement {
  let box = document.querySelector<HTMLElement>('[data-pwa-update]');
  if (box) {
    return box;
  }
  box = document.createElement('aside');
  box.setAttribute('data-pwa-update', '');
  box.className = 'pwa-update';
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.hidden = true;
  box.innerHTML =
    '<p class="banner__title" data-pwa-update-title></p><p data-pwa-update-body></p>' +
    '<button class="btn-primary" type="button" data-pwa-update-accept></button>';
  document.body.append(box);
  return box;
}

function hideHint(): void {
  const hint = document.querySelector<HTMLElement>('[data-pwa-install]');
  if (hint) {
    hint.hidden = true;
  }
}

function fillHint(titulo: string, corpo: string, showAccept: boolean): void {
  const hint = ensureHint();
  const titleEl = hint.querySelector('[data-pwa-install-title]');
  const bodyEl = hint.querySelector('[data-pwa-install-body]');
  const accept = hint.querySelector<HTMLButtonElement>('[data-pwa-install-accept]');
  const dismiss = hint.querySelector<HTMLButtonElement>('[data-pwa-install-dismiss]');
  if (titleEl) {
    titleEl.textContent = titulo;
  }
  if (bodyEl) {
    bodyEl.textContent = corpo;
  }
  if (accept) {
    accept.textContent = INSTALL_COPY.aceitar;
    accept.hidden = !showAccept;
  }
  if (dismiss) {
    dismiss.textContent = INSTALL_COPY.recusar;
  }
  hint.hidden = false;
}

function correlationID(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pwa-${Date.now().toString(36)}`;
}

function pageFeedback(selector: string, titulo: string, corpo: string, proximoPasso: string, kind: 'info' | 'ok' | 'danger'): boolean {
  const feedback = document.querySelector<HTMLElement>(selector);
  if (!feedback) return false;
  feedback.className = `banner banner--${kind}`;
  const title = document.createElement('p');
  title.className = 'banner__title';
  title.textContent = titulo;
  const body = document.createElement('p');
  body.textContent = corpo;
  const next = document.createElement('p');
  next.textContent = proximoPasso;
  feedback.replaceChildren(title, body, next);
  return true;
}

function fallbackFailure(titulo: string, corpo: string, proximoPasso: string): void {
  document.querySelector('[data-pwa-local-error]')?.remove();
  const feedback = document.createElement('aside');
  feedback.setAttribute('data-pwa-local-error', '');
  feedback.className = 'pwa-update banner banner--danger';
  feedback.setAttribute('role', 'alert');
  const title = document.createElement('p');
  title.className = 'banner__title';
  title.textContent = titulo;
  const body = document.createElement('p');
  body.textContent = corpo;
  const next = document.createElement('p');
  next.textContent = proximoPasso;
  feedback.append(title, body, next);
  document.body.append(feedback);
}

function showLocalFailure(action: 'instalar' | 'atualizar', _error: unknown): void {
  const code = correlationID();
  if (action === 'instalar') {
    const shown = pageFeedback(
      '[data-pwa-install-page-feedback]',
      'A instalação não começou',
      'Nenhum atalho foi criado e o acesso atual continua no navegador.',
      `Use o menu do navegador para instalar. Se continuar indisponível, informe o código ${code} ao suporte.`,
      'danger',
    );
    if (!shown) {
      fallbackFailure(
        'A instalação não começou',
        'Nenhum atalho foi criado e o acesso atual continua no navegador.',
        `Abra Instalar aplicativo no menu Mais. Se continuar indisponível, informe o código ${code} ao suporte.`,
      );
    }
    return;
  }
  const shown = pageFeedback(
    '[data-pwa-update-page-feedback]',
    'A atualização não foi aplicada',
    'Os arquivos locais continuam na versão anterior; nenhuma emissão foi reenviada.',
    `Mantenha a conexão e verifique novamente. Se persistir, informe o código ${code} ao suporte.`,
    'danger',
  );
  if (!shown) {
    fallbackFailure(
      'A atualização não foi aplicada',
      'Os arquivos locais continuam na versão anterior; nenhuma emissão foi reenviada.',
      `Abra Verificar atualização no menu do aplicativo. Se persistir, informe o código ${code} ao suporte.`,
    );
  }
}

function syncInstallPage(): void {
  const accept = document.querySelector<HTMLButtonElement>('[data-pwa-install-page-accept]');
  if (!accept) return;
  if (isStandalone()) {
    accept.disabled = true;
    accept.textContent = 'Aplicativo já instalado';
    pageFeedback(
      '[data-pwa-install-page-feedback]',
      'Aplicativo aberto como instalado',
      'Este aparelho já está usando o modo aplicativo.',
      'Você pode voltar ao início e seguir trabalhando.',
      'ok',
    );
    return;
  }
  if (deferredPrompt) {
    accept.disabled = false;
    accept.textContent = INSTALL_COPY.aceitar;
    pageFeedback(
      '[data-pwa-install-page-feedback]',
      'Instalação disponível',
      'O navegador está pronto para criar o atalho neste aparelho.',
      'Selecione Instalar o aplicativo e confirme no navegador.',
      'info',
    );
    return;
  }
  accept.disabled = true;
  accept.textContent = 'Instalação aguardando o navegador';
  if (isIos()) {
    pageFeedback(
      '[data-pwa-install-page-feedback]',
      'Instale pelo menu do Safari',
      'O iPhone ou iPad oferece a instalação pelo menu Compartilhar.',
      'Escolha Adicionar à Tela de Início.',
      'info',
    );
  }
}

function maybeShowInstall(): void {
  syncInstallPage();
  if (isStandalone()) {
    hideHint();
    return;
  }
  if (!isCalmRoute(window.location.pathname)) {
    hideHint();
    return;
  }
  if (Date.now() < dismissedUntil()) {
    hideHint();
    return;
  }
  if (deferredPrompt) {
    fillHint(INSTALL_COPY.titulo, INSTALL_COPY.corpo, true);
    return;
  }
  if (isIos()) {
    fillHint(IOS_COPY.titulo, IOS_COPY.corpo, false);
  }
}

function showUpdate(): void {
  const box = ensureUpdate();
  const titleEl = box.querySelector('[data-pwa-update-title]');
  const bodyEl = box.querySelector('[data-pwa-update-body]');
  const accept = box.querySelector<HTMLButtonElement>('[data-pwa-update-accept]');
  if (titleEl) {
    titleEl.textContent = UPDATE_COPY.titulo;
  }
  if (bodyEl) {
    bodyEl.textContent = UPDATE_COPY.corpo;
  }
  if (accept) {
    accept.textContent = UPDATE_COPY.aceitar;
  }
  box.hidden = false;
}

async function acceptInstall(): Promise<void> {
  const promptEvent = deferredPrompt;
  if (!promptEvent) {
    hideHint();
    throw new Error('o navegador ainda não liberou a instalação');
  }
  deferredPrompt = null;
  await promptEvent.prompt();
  const choice = await promptEvent.userChoice;
  if (choice.outcome !== 'accepted') {
    dismissInstall();
    pageFeedback(
      '[data-pwa-install-page-feedback]',
      'Instalação cancelada',
      'Nenhum atalho foi criado e seus dados não mudaram.',
      'Você pode instalar mais tarde pelo menu do navegador.',
      'info',
    );
    return;
  }
  hideHint();
  pageFeedback(
    '[data-pwa-install-page-feedback]',
    'Instalação confirmada',
    'O navegador aceitou criar o aplicativo neste aparelho.',
    'Abra o JNR NFS-e pela tela inicial quando o ícone aparecer.',
    'ok',
  );
}

async function acceptUpdate(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const waiting = reg?.waiting;
  if (!waiting) {
    await checkForUpdate();
    return;
  }
  reloadOnControllerChange = true;
  pageFeedback(
    '[data-pwa-update-page-feedback]',
    'Aplicando a nova versão',
    'O navegador recebeu a atualização do shell local. Nenhuma operação fiscal será repetida.',
    'Esta página será recarregada uma vez quando a troca terminar.',
    'info',
  );
  waiting.postMessage('SKIP_WAITING');
}

async function checkForUpdate(): Promise<void> {
  const button = document.querySelector<HTMLButtonElement>('[data-pwa-update-check]');
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Verificando versão…';
  }
  try {
    if (!canRegisterSw()) throw new Error('service worker indisponível neste endereço');
    let reg: ServiceWorkerRegistration | null | undefined = await navigator.serviceWorker.getRegistration();
    if (!reg) reg = await registerSw();
    if (!reg) throw new Error('registro local não foi criado');
    await reg.update();
    if (reg.waiting) {
      await acceptUpdate();
      return;
    }
    pageFeedback(
      '[data-pwa-update-page-feedback]',
      'Aplicativo em dia',
      'O navegador não encontrou uma versão mais nova do shell local.',
      'Você pode voltar ao início. Nenhuma emissão foi reenviada.',
      'ok',
    );
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'Verificar e atualizar';
    }
  }
}

function watchRegistration(reg: ServiceWorkerRegistration): void {
  if (reg.waiting && navigator.serviceWorker.controller) {
    showUpdate();
  }
  reg.addEventListener('updatefound', () => {
    const worker = reg.installing;
    if (!worker) {
      return;
    }
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        showUpdate();
      }
    });
  });
}

async function registerSw(): Promise<ServiceWorkerRegistration | null> {
  if (!canRegisterSw()) {
    return null;
  }
  const opts: RegistrationOptions = {
    scope: '/',
    type: 'module',
    updateViaCache: 'none',
  };
  const reg = await navigator.serviceWorker.register('/sw.js', opts);
  watchRegistration(reg);
  return reg;
}

function bindSwLifecycle(): void {
  if (swBound || !('serviceWorker' in navigator)) {
    return;
  }
  swBound = true;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!reloadOnControllerChange) {
      return;
    }
    window.location.reload();
  });
}

export function bindInstall(): void {
  if (!clickBound) {
    clickBound = true;
    window.addEventListener('beforeinstallprompt', (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      deferredPrompt = event;
      syncInstallPage();
      window.setTimeout(() => {
        maybeShowInstall();
      }, 1500);
    });
    window.addEventListener('appinstalled', () => {
      deferredPrompt = null;
      hideHint();
      syncInstallPage();
    });
    document.addEventListener('click', (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('[data-pwa-install-accept]')) {
        event.preventDefault();
        void acceptInstall().catch((error: unknown) => showLocalFailure('instalar', error));
        return;
      }
      if (target.closest('[data-pwa-install-page-accept]')) {
        event.preventDefault();
        void acceptInstall().catch((error: unknown) => showLocalFailure('instalar', error));
        return;
      }
      if (target.closest('[data-pwa-install-dismiss]')) {
        event.preventDefault();
        dismissInstall();
        return;
      }
      if (target.closest('[data-pwa-update-accept]')) {
        event.preventDefault();
        void acceptUpdate().catch((error: unknown) => showLocalFailure('atualizar', error));
      }
    });
    document.addEventListener('submit', (event: SubmitEvent) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-pwa-update-form]')) return;
      event.preventDefault();
      void checkForUpdate().catch((error: unknown) => showLocalFailure('atualizar', error));
    });
  }
  bindSwLifecycle();
  void registerSw()
    .then(() => {
      if (window.location.pathname === '/pwa/atualizar' && new URLSearchParams(window.location.search).get('solicitar') === '1') {
        return checkForUpdate();
      }
      return undefined;
    })
    .catch((error: unknown) => showLocalFailure('atualizar', error));
  if (isIos() && !deferredPrompt) {
    window.setTimeout(() => {
      maybeShowInstall();
    }, 1500);
  }
}

export function refreshInstallHint(): void {
  maybeShowInstall();
}
